/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { Ref, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactMarkdown from 'react-markdown';
import mergeRefs from 'react-merge-refs';

import { postFormData } from '@eika-infrastruktur/request';
import { ButtonTypes, LinkButton, StandardButton } from '@eika/button';
import { Loader } from '@eika/loader';

import { useBankregnr } from '../../config';
import { logger } from '../../logg/logger';
import { getRequestOptions } from '../../meldskade/reducer/skademelding-actions';
import { hentVedleggUrl } from '../../urler';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { isCurrentSporsmalValid } from '../reducer/current-steg';
import {
    addDirectVedleggToSvar,
    buildVedleggFormData,
    deleteVedleggFromSvar,
    updateUploadedVedlegg,
} from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import FileValidation, {
    defaultVarslinger,
    getVarslinger,
    validerValgteFiler,
    Varslinger,
} from './file-validation/file-validation';
import {
    CurrentFilopplastingsporsmal,
    fileToVedlegg,
    UploadedVedleggResult,
    Vedlegg,
    VedleggIdInternal,
    VedleggResultStatus,
    VedleggStatus,
    VedleggWithFile,
    vedleggWithFileToVedlegg,
} from './filopplasting-types';
import { DirectFilopplastingIndicator } from './indicator/direct-filopplasting-indicator';

import './direct-filopplasting.scss';
import './filopplasting-vedlegg.scss';

interface UploadedFileRowProps {
    file: Vedlegg;
    onDelete: () => void;
    status: VedleggStatus;
}

const UploadedFileRow = ({ file, onDelete, status }: UploadedFileRowProps) => (
    <li
        className={classNames('file-upload-list-element', {
            'file-upload-list-element--invalid':
                status === VedleggStatus.Error || status === VedleggStatus.ErrorTotalFileSize,
        })}
    >
        <span className="sgw-icon">
            <span className="icon icon--24 icon--dokument-24" />
        </span>
        <div className="typography-subtitle file-upload-list-element-file-name">{file.fileInfo.name}</div>
        {status === VedleggStatus.Uploading && <Loader mini />}
        {status === VedleggStatus.Ok && <LinkButton onClick={onDelete}>Slett</LinkButton>}
    </li>
);

interface Props {
    currentSporsmal: CurrentFilopplastingsporsmal;
    showFeilmelding: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const DirectFilopplasting = ({ currentSporsmal, showFeilmelding, errorRef, focusOnMount = true }: Props) => {
    const { filer } = currentSporsmal;
    const [varslinger, setVarslinger] = useState<Varslinger>(defaultVarslinger());
    const uploadButtonRef = useFocusRef<HTMLButtonElement>(focusOnMount);
    const dispatch = useSporsmalsflytDispatch();
    const bankregnr = useBankregnr();

    useEffect(() => {
        const uploaderrors = filer.some(
            (f) => f.status === VedleggStatus.ErrorTotalFileSize || f.status === VedleggStatus.Error,
        );

        if (uploaderrors) {
            setVarslinger(getVarslinger([], filer));
        }
    }, [filer]);

    const addVedleggToState = async (vedleggToUpload: Vedlegg[]) =>
        new Promise<void>((resolve) => {
            dispatch(addDirectVedleggToSvar(vedleggToUpload, VedleggResultStatus.Uploading));
            resolve();
        });

    const clearErrors = () => {
        const failedVedleeg = currentSporsmal.filer.filter(
            (v) => v.status === VedleggStatus.Error || v.status === VedleggStatus.ErrorTotalFileSize,
        );
        if (failedVedleeg) {
            dispatch(deleteVedleggFromSvar(failedVedleeg));
        }
        setVarslinger(defaultVarslinger());
    };

    const uploadVedlegg = (
        vedlegg: VedleggWithFile[],
        currentSporsmal: CurrentFilopplastingsporsmal,
        bankregnr: string,
    ) => {
        const existingIds: VedleggIdInternal[] = currentSporsmal.filer
            .filter(
                (f) =>
                    f.status !== VedleggStatus.Error && f.status !== VedleggStatus.ErrorTotalFileSize && f.id !== null,
            )
            .map((f) => {
                return {
                    id: f.id,
                    uploadId: f.uploadId,
                    fileName: f.fileName,
                };
            });

        postFormData(
            hentVedleggUrl(),
            buildVedleggFormData(vedlegg, {
                vedleggIds: existingIds.map((i) => {
                    return {
                        id: i.id,
                        fileName: i.fileName,
                    };
                }),
            }),
            getRequestOptions(bankregnr),
        )
            .then((vedleggResponse: UploadedVedleggResult) => {
                dispatch(updateUploadedVedlegg(vedleggResponse, VedleggResultStatus.Ok));
            })
            .catch((error: Error) => {
                const log = logger(bankregnr);
                log.warn('Failed to upload vedlegg', undefined, undefined, error);

                const vedleggError = vedlegg.map((v) => ({ ...v, status: VedleggStatus.Error }));
                dispatch(updateUploadedVedlegg({ vedlegg: vedleggError }, VedleggResultStatus.Error));
            });
    };

    const upload = (chosenFiles: File[]): void => {
        setVarslinger(getVarslinger(chosenFiles, filer));

        const validFiles: File[] = validerValgteFiler(chosenFiles, filer);

        if (validFiles.length) {
            const vedlegg: VedleggWithFile[] = fileToVedlegg(validFiles);

            addVedleggToState(vedleggWithFileToVedlegg(vedlegg)).then(() => {
                uploadVedlegg(vedlegg, currentSporsmal, bankregnr);
            });
        }
    };

    const deleteUpload = (vedlegg: Vedlegg) => {
        setVarslinger(
            getVarslinger(
                currentSporsmal.filer.map((f) => f.fileInfo),
                filer,
                false,
            ),
        );
        dispatch(deleteVedleggFromSvar([vedlegg]));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: upload });

    return (
        <div className="direct-file-upload-wrapper container__standard-width">
            <div className="file-upload">
                <h2 className="step-header step-header__with-ingress">{currentSporsmal.sporsmal.tittel}</h2>
                <ReactMarkdown>{currentSporsmal.sporsmal.ingress}</ReactMarkdown>
                <div
                    className={classNames(
                        { 'dokumentopplasting--dragActive': isDragActive },
                        { 'dokumentopplasting--dragNotActive': !isDragActive },
                        'filopplaster',
                    )}
                    {...getRootProps()}
                >
                    <input {...getInputProps()} onClick={() => clearErrors()} />
                    <div className="filopplaster__dropzone">
                        <span className="filopplaster__dropzone__buttons">
                            <p>{currentSporsmal.sporsmal.tekst}, eller</p>
                            <StandardButton
                                type="button"
                                buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                ref={mergeRefs([uploadButtonRef, errorRef])}
                            >
                                Velg fil
                            </StandardButton>
                        </span>
                    </div>
                </div>

                {filer && filer.length > 0 && <DirectFilopplastingIndicator vedlegg={filer} />}

                {varslinger && (
                    <div className="file-upload-varslinger">
                        <FileValidation varslinger={varslinger} />
                    </div>
                )}

                {filer && filer.length > 0 && (
                    <>
                        <h3 className="direct-file-upload-list-title typography-h2">Dokumenter</h3>
                        <ul className="direct-file-upload-list">
                            {filer.map((vedlegg) => (
                                <UploadedFileRow
                                    key={vedlegg.uploadId}
                                    file={vedlegg}
                                    onDelete={() => deleteUpload(vedlegg)}
                                    status={vedlegg.status}
                                />
                            ))}
                        </ul>
                    </>
                )}

                {showFeilmelding && !isCurrentSporsmalValid(currentSporsmal) && (
                    <span className="input-text-feilmelding" aria-live="polite">
                        Du må laste opp minst ett dokument.{' '}
                    </span>
                )}
            </div>
        </div>
    );
};
